import { Delete } from '@mui/icons-material';
import { Box, FormControl, IconButton, InputLabel, MenuItem, Select } from '@mui/material';
import React, { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';

import ConfirmationDialog from '../../components/DealGPT/PhaseTwo/ConfirmationDialog';
import { useClassicNavigate } from '../../hooks/useClassicNavigate';
import { useFetchData } from '../../hooks/useFetchData';
import { deleteFilesByPromptId } from '../../redux/actions/fileActions';
import { deletePrompt } from '../../redux/actions/promptActions';
import { AppDispatch } from '../../redux/store';
import { CustomTooltip } from '../../theme/CustomComponents';
import { Prompt } from '../../Types/prompt';
import { promptVersionsPathUrl } from '../../util/routesUrls';
import { useAuth } from '../Providers/AuthProvider';

type PromptVersionSelectProps = {
  promptId: string;
};

const PromptVersionSelect: React.FC<PromptVersionSelectProps> = ({ promptId }) => {
  const navigate = useClassicNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const [versionToDelete, setVersionToDelete] = useState<string | undefined>();
  const { currentUser, isAdmin } = useAuth();

  const { data: versions, refetch: refetchVersions } = useFetchData<Prompt[]>({
    urlPath: promptVersionsPathUrl(promptId),
  });

  const handleDeleteVersion = useCallback(
    async (id: string) => {
      setVersionToDelete(undefined);
      // Delete the specific version
      await dispatch(deletePrompt({ promptId: id, allVersions: false })).unwrap();
      await dispatch(deleteFilesByPromptId(id));

      if (id === promptId) {
        const anotherVersion = versions?.reverse()?.find((v) => v.id !== id);

        if (anotherVersion) {
          navigate(`?promptId=${anotherVersion.id}`);
        }
      }
      // Refetch version list after deletion
      await refetchVersions();
    },
    [dispatch, refetchVersions, promptId, versions, navigate]
  );

  const isUserAllowedToDeletePromptVersion = useCallback(
    (version: Prompt) => {
      if (!currentUser) return false;

      const isOwner = version.ownerId === currentUser.id;
      const isSharedWithUser = Object.keys(version.sharedWith ?? {}).includes(currentUser.id);

      return isAdmin || isOwner || isSharedWithUser;
    },
    [currentUser, isAdmin]
  );

  return (
    <FormControl sx={{ minWidth: 275 }} size="small">
      <InputLabel id="version-select-label">Version</InputLabel>
      <Select
        labelId="version-select-label"
        value={promptId}
        label="Version"
        onChange={(e) => {
          const selectedVersion = e.target.value;
          if (selectedVersion && selectedVersion !== promptId) {
            navigate(`?promptId=${selectedVersion}`);
          }
        }}
        renderValue={(selected) => {
          const selectedVersion = versions?.find((v) => v.id === selected);
          if (!selectedVersion) return '';
          const index = versions?.indexOf(selectedVersion) ?? 0;
          return `v${index + 1} – ${new Date(selectedVersion.createdAt).toLocaleString()}`;
        }}
      >
        {versions?.map((v, i) => (
          <MenuItem
            key={v.id}
            value={v.id}
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              // Ensure icon appears only on hover
              '&:hover .delete-icon': {
                visibility: 'visible',
              },
            }}
          >
            <CustomTooltip
              title={v.createdByName ? `Created by: ${v.createdByName}` : ''}
              placement="left"
            >
              <Box>{`v${i + 1} – ${new Date(v.createdAt).toLocaleString()}`}</Box>
            </CustomTooltip>
            {isUserAllowedToDeletePromptVersion(v) && (
              <IconButton
                aria-label="delete version"
                size="small"
                edge="end"
                className="delete-icon"
                sx={{ visibility: 'hidden' }}
                disabled={versions.length === 1}
                onClick={(e) => {
                  e.stopPropagation(); // Prevent select change
                  setVersionToDelete(v.id);
                }}
              >
                <Delete fontSize="small" />
              </IconButton>
            )}
          </MenuItem>
        ))}
      </Select>
      {versionToDelete && (
        <ConfirmationDialog
          title="Delete prompt version?"
          message="Are you sure you want to delete this prompt version? This action cannot be undone."
          close={() => setVersionToDelete(undefined)}
          confirm={() => handleDeleteVersion(versionToDelete)}
        />
      )}
    </FormControl>
  );
};

export default PromptVersionSelect;
